<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <div class="media-collage-container"
      :class="[alignmentClass, { 'editorial-height': isEditMode }]"
    >
      <media-display
        v-for="media in mediaList"
        class="media-display"
        :class="media.class"
        :key="media.class"
        :sc-image-field="media.imageField"
        :video-source="media.videoSrc"
        :video-loop="media.videoLoop"
        :video-hidden-text="media.videoHiddenText"
      >
        <sh-image :src="media.imageSrc" :alt="media.imageAlt" />
      </media-display>
    </div>
  </sc-render>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import MediaDisplay from '~/components/sitecore/media-display'
import ScRender from '../sc-render'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'ThreeImageGallery',

  components: {
    ScRender,
    MediaDisplay
  },

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      firstImage: 'firstImage',
      firstImageValue: 'firstImage.value',
      secondImage: 'secondImage',
      secondImageValue: 'secondImage.value',
      thirdImage: 'thirdImage',
      thirdImageValue: 'secondImage.value',
      alignmentClass: 'imagesAlignment.value',
      firstVideo: 'firstVideo.value',
      secondVideo: 'secondVideo.value',
      thirdVideo: 'thirdVideo.value',
      firstHiddenVideoTitle: 'firstHiddenVideoTitle.value',
      secondHiddenVideoTitle: 'secondHiddenVideoTitle.value',
      thirdHiddenVideoTitle: 'thirdHiddenVideoTitle.value',
      firstLoop: 'firstLoop.value',
      secondLoop: 'secondLoop.value',
      thirdLoop: 'thirdLoop.value'
    }),

    hasImages () {
      return !!(this.firstImageValue && this.secondImageValue && this.thirdImageValue)
    },

    mediaList () {
      if (!this.hasImages) return []

      const mediaKeys = [
        {
          field: 'firstImage',
          ratio: 'cloudinary_4x3_fill_crop',
          class: 'first',
          videoField: 'firstVideo',
          hiddenText: 'firstHiddenVideoTitle',
          loop: 'firstLoop'
        },
        {
          field: 'secondImage',
          ratio: 'cloudinary_3x4_fill_crop',
          class: 'second',
          videoField: 'secondVideo',
          hiddenText: 'secondHiddenVideoTitle',
          loop: 'secondLoop'
        },
        {
          field: 'thirdImage',
          ratio: 'cloudinary_3x4_fill_crop',
          class: 'third',
          videoField: 'thirdVideo',
          hiddenText: 'thirdHiddenVideoTitle',
          loop: 'thirdLoop'
        }
      ]

      return mediaKeys.map(key => ({
        class: key.class,
        imageField: this[key.field],
        imageSrc: get(this[key.field], ['value', key.ratio]),
        imageAlt: get(this[key.field], 'value.alt'),
        videoSrc: this[key.videoField],
        videoHiddenText: this[key.hiddenText],
        videoLoop: this[key.loop]
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
$ee-placeholder-min-height: 200px;

.editorial-height {
  min-height: $ee-placeholder-min-height;
}

.media-collage-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  @include stack-space($space-090);

  @include tablet {
    flex-direction: row;
    padding: 0 $space-100;
    @include stack-space($space-110);
  }

  .first {
    margin-right: $space-050;
    margin-bottom: $space-090;
    margin-left: $space-070;

    @include tablet {
      @include reset;
      margin-top: $space-060;
    }
  }

  .second {
    margin-left: $space-110;
    margin-bottom: $space-090;
    margin-right: $space-020;

    @include tablet {
      @include reset;
      margin-top: $space-080;
    }
  }

  .third {
    margin-right: $space-080;
    margin-left: $space-050;

    @include tablet {
      @include reset;
    }
  }

  &.bottom {
    align-items: flex-end;

    .first {
      margin-right: $space-070;
      margin-bottom: $space-090;
      margin-left: $space-050;
      @include tablet {
        @include reset;
        margin-bottom: $space-060;
      }
    }

    .second {
      margin-right: $space-110;
      margin-bottom: $space-090;
      margin-left: $space-020;

      @include tablet {
        @include reset;
        margin-bottom: $space-080;
      }
    }

    .third {
      margin-right: $space-050;
      margin-left: $space-080;

      @include tablet {
        @include reset;
      }
    }
  }

  .media-display {
    height: auto;

    @include tablet {
      width: 30%;
    }

    ::v-deep .sc-image-wrapper img {
      width: 100%;
      height: auto;
    }
  }
}
</style>
